import {
  AmountDimension,
  BooleanDimension,
  CalendarDateDimension,
  FinancialRateDimension,
  frMrgaSelectedDimension,
  IntegerDimension,
  NumbersEnumDimension,
  StringsEnumDimension,
  StringsWithDataEnumDimension,
  TextDimension,
} from '@orus.eu/dimensions'
import {
  type FrMrgaClaimsHistoryDuration,
  frMrgaClaimsHistoryDurations,
  type FrMrgaLargestClaim,
  frMrgaLargestClaims,
  frMrgaProductVersions,
} from '@orus.eu/product'
import type { ParameterlessTranslationKey } from '@orus.eu/translations'

export const frMrgaProductVersionDimension = new StringsEnumDimension({
  name: 'frMrgaProductVersion',
  displayKeys: { name: 'dimension_name_fr_mrga_product_version' },
  values: frMrgaProductVersions,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

const lockedInQuoteForClient = { lockedInQuoteFor: { client: frMrgaSelectedDimension } } as const

//#region Business characteristics

export const frMrgaHasTemporaryLoanVehiclesDimension = new BooleanDimension({
  name: 'frMrgaHasTemporaryLoanVehicles',
  displayKeys: { name: 'dimension_name_fr_mrga_has_temporary_loan_vehicles' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaTemporaryLoanVehiclesCountDimension = new StringsWithDataEnumDimension({
  name: 'frMrgaTemporaryLoanVehiclesCount',
  displayKeys: {
    name: 'dimension_name_fr_mrga_temporary_loan_vehicles_count',
    placeholder: 'placeholder_choose_an_answer',
  },
  entries: [
    ['1', { labelKey: 'dimension_label_fr_mrga_temporary_loan_vehicles_count_1' }],
    ['2', { labelKey: 'dimension_label_fr_mrga_temporary_loan_vehicles_count_2' }],
    ['3', { labelKey: 'dimension_label_fr_mrga_temporary_loan_vehicles_count_3' }],
    ['4_or_more', { labelKey: 'dimension_label_fr_mrga_temporary_loan_vehicles_count_4_or_more' }],
  ] as const,
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaHasVehiclesForSaleDimension = new BooleanDimension({
  name: 'frMrgaHasVehiclesForSale',
  displayKeys: { name: 'dimension_name_fr_mrga_has_vehicles_for_sale' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaVehiclesForSaleCountDimension = new IntegerDimension({
  name: 'frMrgaVehiclesForSaleCount',
  displayKeys: { name: 'dimension_name_fr_mrga_vehicles_for_sale_count' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaHasElectricBatteryChargingAreaDimension = new BooleanDimension({
  name: 'frMrgaHasElectricBatteryChargingArea',
  displayKeys: { name: 'dimension_name_fr_mrga_has_electric_battery_charging_area' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaHasPaintBoothDimension = new BooleanDimension({
  name: 'frMrgaHasPaintBooth',
  displayKeys: { name: 'dimension_name_fr_mrga_has_paint_booth' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaHasPhotovoltaicPanelsDimension = new BooleanDimension({
  name: 'frMrgaHasPhotovoltaicPanels',
  displayKeys: { name: 'dimension_name_fr_mrga_has_photovoltaic_panels' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaHasAsbestosDimension = new BooleanDimension({
  name: 'frMrgaHasAsbestos',
  displayKeys: { name: 'dimension_name_fr_mrga_has_asbestos' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaLandlordCompanyNameDimension = new TextDimension({
  name: 'frMrgaLandlordCompanyName',
  displayKeys: { name: 'dimension_name_fr_mrga_landlord_company_name' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

const FR_MRGA_CONTENT_CAPITALS = [50_000, 100_000, 150_000, 200_000] as const

export const frMrgaContentCapitalDimension = new NumbersEnumDimension({
  name: 'frMrgaContentCapital',
  displayKeys: { name: 'dimension_name_fr_mrga_content_capital' },
  values: FR_MRGA_CONTENT_CAPITALS,
  valuesAreAmount: true,
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

const FR_MRGA_VEHICLES_LIMITS_OF_INDEMNITY = [200_000, 320_000] as const

/** The contractual limit of indemnity for the customers' vehicles in the garage's custody, not its own fleet. */
export const frMrgaVehiclesLimitOfIndemnityDimension = new NumbersEnumDimension({
  name: 'frMrgaVehiclesLimitOfIndemnity',
  displayKeys: {
    name: 'dimension_name_fr_mrga_vehicles_limit_of_indemnity',
    hint: 'dimension_hint_fr_mrga_vehicles_limit_of_indemnity',
  },
  values: FR_MRGA_VEHICLES_LIMITS_OF_INDEMNITY,
  valuesAreAmount: true,
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

/** Company created 24 months or less before the start date. */
export const frMrgaIsCompanyCreatorDimension = new BooleanDimension({
  name: 'frMrgaIsCompanyCreator',
  displayKeys: { name: 'dimension_name_fr_mrga_is_company_creator' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

//#endregion

//#region Claims history

export const frMrgaIsCurrentlyInsuredDimension = new BooleanDimension({
  name: 'frMrgaIsCurrentlyInsured',
  displayKeys: { name: 'dimension_name_fr_mrga_is_currently_insured' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaHasBeenPreviouslyInsuredDimension = new BooleanDimension({
  name: 'frMrgaHasBeenPreviouslyInsured',
  displayKeys: { name: 'dimension_name_fr_mrga_has_been_previously_insured' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaInsurancePeriodStartDateDimension = new CalendarDateDimension({
  name: 'frMrgaInsurancePeriodStartDate',
  displayKeys: {
    name: 'dimension_name_fr_mrga_insurance_period_start_date',
    hint: 'dimension_hint_fr_mrga_insurance_period_start_date',
  },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaInsurancePeriodEndDateDimension = new CalendarDateDimension({
  name: 'frMrgaInsurancePeriodEndDate',
  displayKeys: { name: 'dimension_name_fr_mrga_insurance_period_end_date' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaInsurancePeriodStartMaxDateDimension = new CalendarDateDimension({
  name: 'frMrgaInsurancePeriodStartMaxDate',
  displayKeys: { name: 'dimension_name_fr_mrga_insurance_period_start_max_date' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frMrgaInsurancePeriodEndMaxDateDimension = new CalendarDateDimension({
  name: 'frMrgaInsurancePeriodEndMaxDate',
  displayKeys: { name: 'dimension_name_fr_mrga_insurance_period_end_max_date' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frMrgaSinistralityDimension = new BooleanDimension({
  name: 'frMrgaSinistrality',
  displayKeys: { name: 'dimension_name_fr_mrga_sinistrality' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaClaimsCountDimension = new IntegerDimension({
  name: 'frMrgaClaimsCount',
  displayKeys: {
    name: 'dimension_name_fr_mrga_claims_count',
    hint: 'dimension_hint_fr_mrga_claims_count',
  },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaClaimsCostDimension = new AmountDimension({
  name: 'frMrgaClaimsCost',
  displayKeys: { name: 'dimension_name_fr_mrga_claims_cost' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

const frMrgaLargestClaimLabelKeys = {
  none_above_5000: 'dimension_label_fr_mrga_largest_claim_none_above_5000',
  between_5000_and_20000: 'dimension_label_fr_mrga_largest_claim_between_5000_and_20000',
  above_20000: 'dimension_label_fr_mrga_largest_claim_above_20000',
} as const satisfies Record<FrMrgaLargestClaim, ParameterlessTranslationKey>

export const frMrgaLargestClaimDimension = new StringsWithDataEnumDimension({
  name: 'frMrgaLargestClaim',
  displayKeys: {
    name: 'dimension_name_fr_mrga_largest_claim',
    placeholder: 'placeholder_choose_an_answer',
  },
  entries: frMrgaLargestClaims.map((claim) => [claim, { labelKey: frMrgaLargestClaimLabelKeys[claim] }] as const),
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

const frMrgaClaimsHistoryDurationLabelKeys = {
  less_than_12_months: 'dimension_label_fr_mrga_claims_history_duration_less_than_12_months',
  between_12_and_23_months: 'dimension_label_fr_mrga_claims_history_duration_between_12_and_23_months',
  between_24_and_35_months: 'dimension_label_fr_mrga_claims_history_duration_between_24_and_35_months',
  at_least_36_months: 'dimension_label_fr_mrga_claims_history_duration_at_least_36_months',
} as const satisfies Record<FrMrgaClaimsHistoryDuration, ParameterlessTranslationKey>

export const frMrgaClaimsHistoryDurationDimension = new StringsWithDataEnumDimension({
  name: 'frMrgaClaimsHistoryDuration',
  displayKeys: {
    name: 'dimension_name_fr_mrga_claims_history_duration',
    placeholder: 'placeholder_choose_an_answer',
  },
  entries: frMrgaClaimsHistoryDurations.map(
    (duration) => [duration, { labelKey: frMrgaClaimsHistoryDurationLabelKeys[duration] }] as const,
  ),
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

//#endregion

//#region Checkbox statements

// Each answer other than the one the insurer expects blocks the subscription, through the fr-mrga exclusions.
// The insurer expects `false` for the two `frMrgaHad*` questions and `true` for every other one.

export const frMrgaHadContractCancelledByFormerInsurerDimension = new BooleanDimension({
  name: 'frMrgaHadContractCancelledByFormerInsurer',
  displayKeys: { name: 'dimension_name_fr_mrga_had_contract_cancelled_by_former_insurer' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaHadDrivingLicenceWithdrawnDimension = new BooleanDimension({
  name: 'frMrgaHadDrivingLicenceWithdrawn',
  displayKeys: { name: 'dimension_name_fr_mrga_had_driving_licence_withdrawn' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaIsFreeOfInsolvencyProceedingsDimension = new BooleanDimension({
  name: 'frMrgaIsFreeOfInsolvencyProceedings',
  displayKeys: { name: 'dimension_name_fr_mrga_is_free_of_insolvency_proceedings' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaIsHeadOfficeInMetropolitanFranceDimension = new BooleanDimension({
  name: 'frMrgaIsHeadOfficeInMetropolitanFrance',
  displayKeys: { name: 'dimension_name_fr_mrga_is_head_office_in_metropolitan_france' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaIsRiskInMetropolitanFranceDimension = new BooleanDimension({
  name: 'frMrgaIsRiskInMetropolitanFrance',
  displayKeys: { name: 'dimension_name_fr_mrga_is_risk_in_metropolitan_france' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaIsBuildingMadeOfHardMaterialsDimension = new BooleanDimension({
  name: 'frMrgaIsBuildingMadeOfHardMaterials',
  displayKeys: { name: 'dimension_name_fr_mrga_is_building_made_of_hard_materials' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaIsBuildingWithinHeightLimitDimension = new BooleanDimension({
  name: 'frMrgaIsBuildingWithinHeightLimit',
  displayKeys: { name: 'dimension_name_fr_mrga_is_building_within_height_limit' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaIsOutsideShoppingCenterDimension = new BooleanDimension({
  name: 'frMrgaIsOutsideShoppingCenter',
  displayKeys: { name: 'dimension_name_fr_mrga_is_outside_shopping_center' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaIsActivityLimitedToLightVehiclesDimension = new BooleanDimension({
  name: 'frMrgaIsActivityLimitedToLightVehicles',
  displayKeys: { name: 'dimension_name_fr_mrga_is_activity_limited_to_light_vehicles' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

export const frMrgaIsAwayFromHazardousNeighboursDimension = new BooleanDimension({
  name: 'frMrgaIsAwayFromHazardousNeighbours',
  displayKeys: { name: 'dimension_name_fr_mrga_is_away_from_hazardous_neighbours' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: lockedInQuoteForClient,
} as const)

//#endregion

//#region Underwriting checks

/** Filled in the back-office after the address risk check (flood, industrial risk, listed monument, etc.) */
export const frMrgaIsRiskSensitivityCheckPassedDimension = new BooleanDimension({
  name: 'frMrgaIsRiskSensitivityCheckPassed',
  displayKeys: { name: 'dimension_name_fr_mrga_is_risk_sensitivity_check_passed' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: { lockedInQuoteFor: { client: frMrgaSelectedDimension, partner: frMrgaSelectedDimension } },
} as const)

//#endregion

//#region Back-office adjustments

export const frMrgaCustomActivityDimension = new TextDimension({
  name: 'frMrgaCustomActivity',
  displayKeys: { name: 'dimension_name_fr_mrga_custom_activity', hint: 'dimension_hint_fr_mrga_custom_activity' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frMrgaCustomSummaryDimension = new TextDimension({
  name: 'frMrgaCustomSummary',
  displayKeys: { name: 'dimension_name_fr_mrga_custom_summary', hint: 'dimension_hint_fr_mrga_custom_summary' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

/** Applied to every Axeria guarantee; the assistance, the legal protection and the FGTI keep their own price */
export const frMrgaOverchargeRateDimension = new FinancialRateDimension({
  name: 'frMrgaOverchargeRate',
  displayKeys: { name: 'dimension_name_fr_mrga_overcharge_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: true,
  inputLocking: undefined,
  configuration: { min: 0, max: 10, step: 0.01 },
})

//#endregion
